import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Receipt, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { taxSettingsService } from '@/services/platform/tax-settings';
import type { TaxPricingMode } from '@/types/orders';

/**
 * Settings → Tax preferences.
 *
 * Zoho-style tenant-level switch for how operator-entered `unitPrice`
 * is interpreted:
 *   • Tax-inclusive (MRP) — common for apparel + retail + B2C portals.
 *   • Tax-exclusive — common for B2B + services where tax is added at invoice.
 *
 * Flipping this only affects *future* orders; historical orders keep
 * the mode they were stamped with at create time. This is enforced on
 * the BE — see `SalesOrder.taxPricingMode`.
 */
export function TaxSettingsPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['tenant', 'tax-settings'],
    queryFn: taxSettingsService.get,
  });

  const [mode, setMode] = useState<TaxPricingMode>('EXCLUSIVE');
  useEffect(() => {
    if (settingsQuery.data) setMode(settingsQuery.data.taxPricingMode);
  }, [settingsQuery.data]);

  const dirty = settingsQuery.data && mode !== settingsQuery.data.taxPricingMode;

  const mutation = useMutation({
    mutationFn: () => taxSettingsService.update({ taxPricingMode: mode }),
    onSuccess: () => {
      toast.success('Tax preference updated');
      qc.invalidateQueries({ queryKey: ['tenant', 'tax-settings'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update'),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-8">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <Receipt className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-[20px] font-semibold leading-tight tracking-tight">
              Tax preferences
            </h1>
            <p className="text-[13px] text-muted-foreground">
              How prices you enter on orders relate to tax. Affects new orders only —
              historical orders keep the mode they were created under.
            </p>
          </div>
        </div>
      </header>

      <section className="space-y-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Pricing mode
        </div>
        <div className="space-y-2.5">
          <ModeOption
            value="EXCLUSIVE"
            current={mode}
            label="Tax-exclusive prices"
            blurb="Unit price you enter is the net amount. Tax is computed on top at invoice generation (e.g. ₹1,000 entered + 18% GST = ₹1,180 invoiced)."
            example="₹ 1,000 net + 18% GST = ₹ 1,180"
            audience="Typical for B2B sales, services, and wholesale."
            onSelect={() => setMode('EXCLUSIVE')}
          />
          <ModeOption
            value="INCLUSIVE"
            current={mode}
            label="Tax-inclusive prices (MRP)"
            blurb="Unit price you enter already contains tax. The invoice shows the embedded GST extracted (e.g. ₹1,180 MRP = ₹1,000 net + ₹180 GST)."
            example="₹ 1,180 MRP includes ₹ 180 GST"
            audience="Typical for apparel, retail, e-commerce, and B2C portals."
            onSelect={() => setMode('INCLUSIVE')}
          />
        </div>
      </section>

      <div className="flex items-center justify-between pt-3">
        <div className="text-[11.5px] text-muted-foreground">
          {settingsQuery.data ? (
            <>
              Current saved mode:{' '}
              <Badge variant="muted">
                {settingsQuery.data.taxPricingMode === 'INCLUSIVE'
                  ? 'Tax-inclusive (MRP)'
                  : 'Tax-exclusive'}
              </Badge>
            </>
          ) : (
            'Loading…'
          )}
        </div>
        <Button
          size="sm"
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : 'Save preference'}
        </Button>
      </div>

      <div className="rounded-md border border-info/20 bg-info/5 px-4 py-3 text-[12px] leading-relaxed text-muted-foreground">
        <strong className="text-foreground">Note:</strong> switching the mode does not
        rewrite past orders. Each sales order is stamped at create time with the
        mode that was active then; the invoice generated from it locks the same
        mode. To migrate an existing order to the other mode, cancel and
        re-create it.
      </div>
    </div>
  );
}

function ModeOption({
  value,
  current,
  label,
  blurb,
  example,
  audience,
  onSelect,
}: {
  value: TaxPricingMode;
  current: TaxPricingMode;
  label: string;
  blurb: string;
  example: string;
  audience: string;
  onSelect: () => void;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-md border bg-card px-4 py-3.5 text-left transition-colors ${
        active
          ? 'border-primary/40 ring-1 ring-inset ring-primary/15'
          : 'border-border/70 hover:border-border'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${
            active ? 'border-primary bg-primary' : 'border-muted-foreground/40'
          }`}
        >
          {active && (
            <CheckCircle2 className="h-4 w-4 fill-primary text-primary-foreground -translate-x-px -translate-y-px" />
          )}
        </div>
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-semibold">{label}</span>
          </div>
          <p className="text-[12.5px] leading-relaxed text-muted-foreground">{blurb}</p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Badge variant="muted" className="font-mono text-[10.5px]">
              {example}
            </Badge>
            <span className="text-[11px] text-muted-foreground">{audience}</span>
          </div>
        </div>
      </div>
    </button>
  );
}
